import { parseISO } from 'date-fns'
import moment, { Moment } from 'moment'
import { DateLike } from 'utils'

export function isToday(value: DateLike) {
  return moment().isSame(value, 'day')
}

export function dateLikeCompareAscendingFn(time1: DateLike, time2: DateLike) {
  return time1 < time2 ? -1 : time1 > time2 ? 1 : 0
}

export function dateLikeCompareDescendingFn(time1: DateLike, time2: DateLike) {
  return time1 < time2 ? 1 : time1 > time2 ? -1 : 0
}

export function isWeekday(value: Moment) {
  return value.day() !== 6 && value.day() !== 0
}

export function isTradingDay(date: DateLike, holidays: DateLike[]) {
  const dateMoment = moment(date)
  const isHoliday = holidays.some(holiday => dateMoment.isSame(holiday, 'day'))
  return isWeekday(dateMoment) && !isHoliday
}

export function getPrevTradingDay(date: DateLike, holidays: DateLike[], inclusive?: boolean): Date {
  let prevTradingDay = moment(date).subtract(inclusive ? 0 : 1, 'day')

  while (!isTradingDay(prevTradingDay.toDate(), holidays)) {
    prevTradingDay.subtract(1, 'day')
  }

  return prevTradingDay.toDate()
}

export function getNextTradingDay(date: DateLike, holidays: DateLike[], inclusive?: boolean): Date {
  let nextTradingDay = moment(date).add(inclusive ? 0 : 1, 'day')

  while (!isTradingDay(nextTradingDay.toDate(), holidays)) {
    nextTradingDay.add(1, 'day')
  }

  return nextTradingDay.toDate()
}

export function filterWeekend(date: Date) {
  return isWeekday(moment(date))
}

export function addTradingDays(date: DateLike, days: number, holidays: DateLike[]) {
  let daysRemaining = days
  const newDate = moment(date)

  while (daysRemaining !== 0) {
    newDate.add(1, 'day')
    if (isTradingDay(newDate.toDate(), holidays)) {
      daysRemaining--
    }
  }

  return newDate.toDate()
}

export function getDateString(date: DateLike) {
  return new Date(date).toDateString()
}

export function toLocalDate(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

export function toLocalDateString(date: Date | string | null | undefined): string {
  return moment(date ?? undefined).format('YYYY-MM-DD')
}

export function toDateAndMonthString(date: Date): string {
  return moment(date).format('D-MMM')
}

export function getPreviousBusinessDay(date: DateLike) {
  let previousDay = new Date(date)
  previousDay.setDate(previousDay.getDate() - 1)

  if (previousDay.getDay() === 0) {
    previousDay.setDate(previousDay.getDate() - 2)
  } else if (previousDay.getDay() === 6) {
    previousDay.setDate(previousDay.getDate() - 1)
  }

  return previousDay
}

/**
 * helper to sync the offset hours a local time zone has
 * with UTC */
export function localToUTC(date: Date): Date {
  const utc = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  )

  return new Date(utc)
}

export function parseDateLike(dateLike: DateLike): Date {
  if (typeof dateLike === 'string') {
    const result = parseISO(dateLike)

    if (result.toString() === 'Invalid Date') {
      throw new Error(`Incoming DateLike in unaccepted string format: ${dateLike}`)
    }

    return result
  }

  return dateLike
}
